import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, IconButton, Paper, CircularProgress, Avatar, InputAdornment, Divider, Chip, Stack, Tooltip, useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import workplaceService from '../services/workplace.service';
import AddWorkplaceDrawer from './AddWorkplaceDrawer';
import WorkplaceDetailDrawer from './WorkplaceDetailDrawer';
import { usePreferences } from '../contexts/PreferencesContext';
import { getThemeColors } from '../config/theme';

const WorkplaceManagement = () => {
    const theme = useTheme();
    const { t, isDarkMode } = usePreferences();
    const shellColors = getThemeColors(isDarkMode);
    const [workplaces, setWorkplaces] = useState([]);
    const [selectedWorkplace, setSelectedWorkplace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
    const [isDetailDrawerOpen, setDetailDrawerOpen] = useState(false);

    useEffect(() => {
        fetchWorkplaces();
    }, []);

    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        if (nameParts.length > 1) {
            return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
        }
        if (nameParts[0] && nameParts[0].length > 1) {
            return nameParts[0].substring(0, 2).toUpperCase();
        }
        return (name[0] || '').toUpperCase();
    };

    const fetchWorkplaces = () => {
        setLoading(true);
        workplaceService.getWorkplaces().then((response) => {
            const freshWorkplaces = response.data;
            setWorkplaces(freshWorkplaces);
            if (selectedWorkplace) {
                const updatedWp = freshWorkplaces.find((wp) => wp.id === selectedWorkplace.id);
                setSelectedWorkplace(updatedWp || null);
            }
        }).catch((error) => console.error('Error fetching workplaces', error))
            .finally(() => setLoading(false));
    };

    const handleWorkplaceCreated = () => {
        fetchWorkplaces();
        setAddDrawerOpen(false);
    };

    const handleWorkplaceUpdated = () => {
        fetchWorkplaces();
    };

    const handleDeleteWorkplace = (id) => {
        workplaceService.deleteWorkplace(id).then(() => {
            fetchWorkplaces();
        }).catch((error) => console.error('Error deleting workplace', error));
    };

    const handleOpenDetailDrawer = (workplace) => {
        setSelectedWorkplace(workplace);
        setDetailDrawerOpen(true);
    };

    const handleCloseDetailDrawer = () => {
        setDetailDrawerOpen(false);
        setSelectedWorkplace(null);
    };

    const filteredWorkplaces = workplaces.filter((wp) =>
        wp.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const subtitleText = searchQuery && filteredWorkplaces.length !== workplaces.length
        ? `${filteredWorkplaces.length} / ${workplaces.length} ${t('pages.workplaces.count')}`
        : `${workplaces.length} ${t('pages.workplaces.count')}`;

    if (loading) {
        return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
    }

    return (
        <Box sx={{ margin: 'auto', maxWidth: 1200, bgcolor: 'background.default' }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    px: { xs: 2, sm: 3 },
                    pt: { xs: 2, sm: 3 },
                    pb: 2,
                    bgcolor: shellColors.shellBackground || shellColors.background || '#F3F4F6',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        backgroundImage: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: isDarkMode
                            ? '0 8px 24px rgba(0, 0, 0, 0.35)'
                            : '0 4px 20px rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                                {t('pages.workplaces.title')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {subtitleText}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => setAddDrawerOpen(true)}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'primary.dark' },
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>

                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={t('pages.workplaces.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{
                            mb: searchQuery ? 1.5 : 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '16px',
                                backgroundColor: 'background.paper',
                                '&.Mui-focused': {
                                    boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.4)' : 'rgba(37, 99, 235, 0.3)'}`,
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {searchQuery && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                            <Chip
                                label={`${t('common.search')}: "${searchQuery}"`}
                                onDelete={() => setSearchQuery('')}
                                size="small"
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                        gap: 3,
                    }}
                >
                    {filteredWorkplaces.length > 0 ? filteredWorkplaces.map((wp) => (
                        <Paper
                            key={wp.id}
                            onClick={() => handleOpenDetailDrawer(wp)}
                            sx={{
                                p: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                borderRadius: 4,
                                boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
                                transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.palette.mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.5)' : '0 6px 16px rgba(0,0,0,0.1)',
                                },
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, flexGrow: 1 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 50, height: 50, fontSize: '1.2rem', fontWeight: 'bold' }}>
                                    {getInitials(wp.name)}
                                </Avatar>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', ml: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {wp.name}
                                </Typography>
                            </Box>

                            <Box sx={{ px: 2, pb: 2 }}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title="Geofencing Status">
                                            <PublicIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </Tooltip>
                                        <Chip
                                            label={wp.geofencing_enabled ? `Enabled: ${wp.geofencing_radius}m` : 'Disabled'}
                                            color={wp.geofencing_enabled ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                    {!!wp.geofencing_enabled && wp.latitude && wp.longitude && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Tooltip title="Location">
                                                <LocationOnOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            </Tooltip>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {`${parseFloat(wp.latitude).toFixed(4)}, ${parseFloat(wp.longitude).toFixed(4)}`}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>
                    )) : (
                        <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary', gridColumn: '1 / -1' }}>
                            {t('pages.workplaces.noResults')}
                        </Typography>
                    )}
                </Box>
            </Box>

            <AddWorkplaceDrawer
                open={isAddDrawerOpen}
                onClose={() => setAddDrawerOpen(false)}
                onWorkplaceCreated={handleWorkplaceCreated}
            />

            <WorkplaceDetailDrawer
                workplace={selectedWorkplace}
                open={isDetailDrawerOpen}
                onClose={handleCloseDetailDrawer}
                onWorkplaceDeleted={handleDeleteWorkplace}
                onWorkplaceUpdated={handleWorkplaceUpdated}
            />
        </Box>
    );
};

export default WorkplaceManagement;
